import { ThumbsUp, ThumbsDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { CopyButton } from '@/components/copy-button';
import { useLanguage } from '@/contexts/language-context';
import { logger } from '@/lib/logger';

interface FeedbackButtonsProps {
  traceId: string | null;
  feedbackStatus: 'positive' | 'negative' | null;
  onFeedbackClick: (feedback: 'positive' | 'negative') => void;
  contentToCopy?: string;
  showCopyButton?: boolean;
}

export function FeedbackButtons({ traceId, feedbackStatus, onFeedbackClick, contentToCopy, showCopyButton = true }: FeedbackButtonsProps) {
  const { t } = useLanguage();

  const handleClick = (feedback: 'positive' | 'negative') => {
    if (!traceId) {
      logger.info('Feedback button clicked but no trace ID available');
    }
    onFeedbackClick(feedback);
  };

  return (
    <div className="flex gap-2 mt-3">
      {/* Copy Button */}
      {showCopyButton && contentToCopy && (
        <CopyButton
          text={contentToCopy}
          className="bg-white/8 border border-white/16 text-[#ccc] hover:bg-white/16 hover:text-white"
        />
      )}

      {/* Thumbs Up Button */}
      {(feedbackStatus === null || feedbackStatus === 'positive') && (
        <Button
          variant={feedbackStatus === 'positive' ? 'ghost' : 'outline'}
          size="sm"
          onClick={() => handleClick('positive')}
          className={`!p-1.5 transition-all duration-200 ${feedbackStatus === 'positive' ? 'border border-white/16 hover:bg-[rgba(249,135,21,0.15)] bg-[rgba(249,135,21,0.15)] text-[#F98715]' : 'text-[#ccc] hover:bg-[rgba(249,135,21,0.15)] hover:text-[#F98715] border-white/16 bg-white/8'}`}
          aria-label={t('common.helpful')}
          title={t('common.helpful')}>
          <ThumbsUp
            className={`w-4 h-4 ${feedbackStatus === 'positive' ? 'text-[#F98715]' : ''}`}
            strokeWidth={1.5}
          />
        </Button>
      )}

      {/* Thumbs Down Button */}
      {(feedbackStatus === null || feedbackStatus === 'negative') && (
        <Button
          variant={feedbackStatus === 'negative' ? 'ghost' : 'outline'}
          size="sm"
          onClick={() => handleClick('negative')}
          className={`!p-1.5 transition-all duration-200 ${feedbackStatus === 'negative' ? 'border border-white/16 bg-[rgba(249,135,21,0.15)] text-[#F98715]' : 'text-[#ccc] border-white/16 hover:bg-[rgba(249,135,21,0.15)] hover:text-[#F98715] bg-white/8'}`}
          aria-label={t('common.notHelpful')}
          title={t('common.notHelpful')}>
          <ThumbsDown
            className={`w-4 h-4 ${feedbackStatus === 'negative' ? 'text-[#F98715]' : ''}`}
            strokeWidth={1.5}
          />
        </Button>
      )}
    </div>
  );
}
